import {
  createSHA256SaltedHash,
  decryptSymmetric,
  encryptAsymmetric,
  encryptSymmetric,
  generateSymmetricKey,
  getPasswordStrength,
} from '@uniqkey-frontend/shared-app';
import {
  type VaultPasswordCreationRequestedForEmployeePayload,
  type VaultPasswordCreationRequestedForGroupPayload,
} from '@uniqkey-backend-organization-web/api-client';
import type { ITrustedPortalHandlerParams } from '../../../../interfaces';
import { getSymmetricKey } from '../../../../../../services/companionApplicationService';

type TPassword = VaultPasswordCreationRequestedForEmployeePayload['password']
  | VaultPasswordCreationRequestedForGroupPayload['password'];

interface IHandleAdminVaultPasswordCreationParams extends Pick<
  ITrustedPortalHandlerParams, 'organizationPublicKey' | 'organizationPrivateKey'
> {
  password: TPassword;
  errorPrefix: string;
}

const handleAdminVaultPasswordCreation = async (
  params: IHandleAdminVaultPasswordCreationParams,
) => {
  const {
    organizationPublicKey, organizationPrivateKey, password, errorPrefix,
  } = params;

  const companionApplicationSymmetricKey = getSymmetricKey();
  if (!companionApplicationSymmetricKey) {
    throw new Error(`${errorPrefix} companionApplicationSymmetricKey not found`);
  }

  const symmetricKey = generateSymmetricKey();

  let encryptedPassword: null | string = null;
  let organizationPasswordHash: null | string = null;
  let strength = 0;

  if (password) {
    const decryptedPassword = decryptSymmetric({
      cipher: password,
      key: companionApplicationSymmetricKey,
    });

    encryptedPassword = encryptSymmetric({
      string: decryptedPassword,
      key: symmetricKey,
    });

    strength = getPasswordStrength(decryptedPassword);

    organizationPasswordHash = createSHA256SaltedHash(decryptedPassword, organizationPrivateKey);
  }

  const encryptedSymmetricKeyBackup = await encryptAsymmetric({
    string: symmetricKey,
    publicKey: organizationPublicKey,
  });

  return {
    encryptedPassword,
    strength,
    symmetricKey,
    encryptedSymmetricKeyBackup,
    organizationPasswordHash,
  };
};

export default handleAdminVaultPasswordCreation;
